import React, { useContext, useEffect, useState } from "react";
import logo from "assets/img/logos/barlleno-logo-blanco.png";
import { useNavigate, useLocation } from 'react-router-dom';
import UserContextProvider from "interface/interfaces";
import { sessionService } from "service/sessionService";
import { useLocalStorage } from "hooks/useLocalStorage";
import menuIcon from "assets/icons/menu.svg";
import ClearIcon from '@mui/icons-material/Clear';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import TicketFilterBar from "components/TicketFilterBar/TicketFilterBar";

const NavBar = ({ showButtons }: any) => {
  const [sessionLog, setSessionLog] = useState(false);
  const context = useContext(UserContextProvider);
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setActive] = useState(false);
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = useState(false);
  const { removeItem } = useLocalStorage();

  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  // 1) Efecto inicial
  useEffect(() => {
    const userCurrent = sessionService.getCurrentUser();
    if (userCurrent != null) {
      setSessionLog(true);
      context.asignateUser(userCurrent);
    } else {
      setSessionLog(false);
    }
  }, []);

  // 2) Reacciona cuando cambia el contexto
  useEffect(() => {
    const hasCtx = !!context?.user && !!(context.user.id || context.user.uid);
    setSessionLog(hasCtx);
  }, [context?.user]);

  // 3) Reacciona a eventos de sesión
  useEffect(() => {
    const handleAuthChanged = () => {
      const u = sessionService.getCurrentUser();
      setSessionLog(!!u && !!(u.id || u.uid));
      try { context.asignateUser?.(u); } catch { }
    };

    window.addEventListener("storage", handleAuthChanged);
    window.addEventListener("auth:changed", handleAuthChanged);
    return () => {
      window.removeEventListener("storage", handleAuthChanged);
      window.removeEventListener("auth:changed", handleAuthChanged);
    };
  }, [context]);

  const logout = () => {
    setSessionLog(false);
    sessionService.destroySession();
    removeItem("user");
    try { context.asignateUser(null as any); } catch { }
    navigate("/");
  };

  const redirect = (option: string) => {
    option === 'login' ? navigate("/login") : navigate("/register/");
  };

  const goToSection = (sectionId: string) => {
    const onHome = window.location.pathname === '/home' || window.location.pathname === '/';

    if (onHome) {
      if (sectionId === 'inicio') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const el = document.getElementById(sectionId) ||
        document.getElementById(sectionId === 'sobre-nosotros' ? 'about-us' : sectionId) ||
        document.querySelector(`[data-anchor="${sectionId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      const targetHash = sectionId === 'sobre-nosotros' ? 'about-us' : sectionId;
      navigate(
        { pathname: '/home', hash: `#${targetHash}` },
        { state: { anchor: targetHash }, replace: false }
      );
    }
  };

  return (
    <>
      <div
        className="fixed top-0 left-0 z-[40] w-full"
        style={{ background: "linear-gradient(180deg, #0F1525 0%, rgba(15, 21, 37, 0) 100%)" }}
      >
        {/* Desktop navbar */}
        <div className="hidden md:block p-8 lg:p-6">
          <div className="mx-auto lg:mx-[5rem] px-6">
            {/* 3 columnas: [logo] [links centrados] [acciones derecha] */}
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-6 h-20">
              {/* LOGO (izquierda) */}
              <div className="min-w-[15rem] flex items-center">
                <img
                  src={logo}
                  alt="Logo barllenotickets"
                  className="w-[10rem] cursor-pointer"
                  onClick={() => navigate("/")}
                />
              </div>

              {/* LINKS (centro) — se mantienen centrados y sin saltos */}
              <nav
                className="flex items-center justify-center font-semibold text-white gap-8 select-none"
              >
                <button
                  onClick={() => goToSection('inicio')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Inicio
                </button>

                <button
                  onClick={() => goToSection('about-us')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Sobre nosotros
                </button>

                {/* <button
                  onClick={() => goToSection('faqs')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  FAQs
                </button> */}

                <button
                  onClick={() => navigate('/crear')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Crear
                </button>
              </nav>

              {/* ACCIONES (derecha) — ancho fijo para que no mueva nada */}
              <div className="w-[340px] flex items-center justify-end gap-3">
                {sessionLog ? (
                  <div>
                    <button
                      onClick={() => setIsProfileSidebarOpen(true)}
                      className="flex items-center gap-2 h-12 px-4 rounded-full bg-graffito/80 border border-primary/50 text-white font-semibold transition hover:bg-graffito hover:border-primary cursor-pointer"
                    >
                      {context.user?.image ? (
                        <div className="w-8 h-8 rounded-full bg-cover bg-center border border-primary" style={{ backgroundImage: `url(${context.user.image})` }} />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                          {context.user?.name ? context.user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                      )}
                      <span className="max-w-[100px] truncate">{context.user?.name || 'Usuario'}</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => redirect('login')}
                      className="h-10 px-5 rounded-full border-2 border-primary bg-[#2c2b30] text-white font-bold transition hover:bg-primary/20 flex items-center justify-center whitespace-nowrap"
                    >
                      Iniciar Sesión
                    </button>
                    <button
                      onClick={() => redirect('register')}
                      className="h-10 px-5 rounded-full bg-primary text-white font-bold shadow-[0_0_15px_rgba(232,65,55,0.5)] transition hover:bg-primary/90 uppercase flex items-center justify-center whitespace-nowrap"
                    >
                      REGISTRARSE
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Barra de Filtro de Boletos (Únicamente al iniciar sesión, en la página home y en pantallas grandes) */}
            {sessionLog && isHomePage && <TicketFilterBar />}
          </div>
        </div>

        {/* Mobile Header */}
        <div className="w-full justify-between items-center flex md:hidden relative px-6 py-4">
          <div>
            <img
              src={logo}
              alt="Logo barllenotickets"
              className="w-32 cursor-pointer"
              onClick={() => navigate("/home")}
            />
          </div>
          <button
            onClick={() => setActive(true)}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 active:scale-95 transition-all text-white flex items-center justify-center cursor-pointer border border-white/10"
            aria-label="Abrir menú"
          >
            <img src={menuIcon} className="w-6 h-6" alt="Menú" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <div
        className={`fixed inset-0 z-[100] md:hidden transition-all duration-300 ${
          isActive ? "opacity-100 pointer-events-auto visible" : "opacity-0 pointer-events-none invisible"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity duration-300 ${
            isActive ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setActive(false)}
        />

        {/* Drawer Panel */}
        <div
          className={`fixed top-0 right-0 h-full w-[85vw] max-w-[340px] bg-[#121318]/95 backdrop-blur-2xl border-l border-white/10 shadow-[-10px_0_40px_rgba(0,0,0,0.9)] z-[101] flex flex-col justify-between p-6 transition-transform duration-300 ease-out transform ${
            isActive ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Top: Header with Logo & Close button */}
          <div>
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <img
                src={logo}
                alt="Logo barllenotickets"
                className="w-28 cursor-pointer"
                onClick={() => {
                  setActive(false);
                  navigate("/");
                }}
              />
              <button
                onClick={() => setActive(false)}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 active:scale-90 flex items-center justify-center text-gray-300 hover:text-white transition-all cursor-pointer border border-white/10"
                aria-label="Cerrar menú"
              >
                <ClearIcon className="!text-xl" />
              </button>
            </div>

            {/* Profile Info (When logged in) */}
            {sessionLog && (
              <div className="flex items-center gap-3 p-3.5 mt-4 rounded-2xl bg-white/5 border border-white/10 shadow-inner">
                {context.user?.image ? (
                  <div
                    className="w-11 h-11 rounded-full bg-cover bg-center border-2 border-primary shrink-0"
                    style={{ backgroundImage: `url(${context.user.image})` }}
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-white text-base font-bold shrink-0 shadow-md">
                    {context.user?.name ? context.user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="text-white font-bold text-sm truncate">
                    {context.user?.name} {context.user?.lastname}
                  </p>
                  <p className="text-gray-400 text-xs truncate">{context.user?.email}</p>
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <nav className="flex flex-col gap-1.5 mt-5">
              <button
                onClick={() => {
                  setActive(false);
                  goToSection('inicio');
                }}
                className="flex items-center justify-between px-4 py-3 rounded-xl text-left font-semibold text-sm text-gray-200 hover:text-white hover:bg-white/10 active:bg-white/15 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <HomeOutlinedIcon className="text-primary group-hover:scale-110 transition-transform !text-xl" />
                  <span>Inicio</span>
                </div>
                <ChevronRightIcon className="text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all !text-lg" />
              </button>

              <button
                onClick={() => {
                  setActive(false);
                  goToSection('about-us');
                }}
                className="flex items-center justify-between px-4 py-3 rounded-xl text-left font-semibold text-sm text-gray-200 hover:text-white hover:bg-white/10 active:bg-white/15 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <InfoOutlinedIcon className="text-primary group-hover:scale-110 transition-transform !text-xl" />
                  <span>Sobre nosotros</span>
                </div>
                <ChevronRightIcon className="text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all !text-lg" />
              </button>

              <button
                onClick={() => {
                  setActive(false);
                  navigate('/crear');
                }}
                className="flex items-center justify-between px-4 py-3 rounded-xl text-left font-semibold text-sm text-gray-200 hover:text-white hover:bg-white/10 active:bg-white/15 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <AddCircleOutlineIcon className="text-primary group-hover:scale-110 transition-transform !text-xl" />
                  <span>Crear</span>
                </div>
                <ChevronRightIcon className="text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all !text-lg" />
              </button>

              {sessionLog && (
                <>
                  <button
                    onClick={() => {
                      setActive(false);
                      navigate('/profile');
                    }}
                    className="flex items-center justify-between px-4 py-3 rounded-xl text-left font-semibold text-sm text-gray-200 hover:text-white hover:bg-white/10 active:bg-white/15 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <PersonOutlinedIcon className="text-primary group-hover:scale-110 transition-transform !text-xl" />
                      <span>Mi Perfil</span>
                    </div>
                    <ChevronRightIcon className="text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all !text-lg" />
                  </button>

                  <button
                    onClick={() => {
                      setActive(false);
                      navigate('/my-tickets');
                    }}
                    className="flex items-center justify-between px-4 py-3 rounded-xl text-left font-semibold text-sm text-gray-200 hover:text-white hover:bg-white/10 active:bg-white/15 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <ConfirmationNumberIcon className="text-primary group-hover:scale-110 transition-transform !text-xl" />
                      <span>Mis Tickets</span>
                    </div>
                    <ChevronRightIcon className="text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all !text-lg" />
                  </button>
                </>
              )}
            </nav>
          </div>

          {/* Bottom Actions */}
          <div className="border-t border-white/10 pt-5 mt-auto">
            {sessionLog ? (
              <button
                onClick={() => {
                  setActive(false);
                  logout();
                }}
                className="w-full h-12 rounded-full bg-[#E44036] hover:bg-[#c93229] active:scale-95 text-white font-bold uppercase text-xs tracking-wider shadow-lg shadow-red-900/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <LogoutIcon className="!text-lg" />
                <span>Cerrar sesión</span>
              </button>
            ) : showButtons ? (
              <div className="flex flex-col gap-3 w-full">
                <button
                  onClick={() => {
                    setActive(false);
                    navigate("/login");
                  }}
                  className="w-full h-11 px-4 rounded-full border-2 border-primary bg-[#2c2b30] hover:bg-primary/20 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shadow-md"
                >
                  <LoginIcon className="!text-lg text-primary" />
                  <span>Iniciar Sesión</span>
                </button>

                <button
                  onClick={() => {
                    setActive(false);
                    navigate("/register");
                  }}
                  className="w-full h-11 px-4 rounded-full bg-primary hover:bg-[#c93229] active:scale-95 text-white font-extrabold uppercase text-xs tracking-wider shadow-[0_0_15px_rgba(232,65,55,0.5)] transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <PersonAddAltOutlinedIcon className="!text-lg" />
                  <span>REGISTRARSE</span>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Sidebar de Perfil (Drawer Lateral) */}
      {isProfileSidebarOpen && (
        <>
          {/* Fondo oscuro para cerrar al dar click afuera */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90] transition-opacity cursor-pointer"
            onClick={() => setIsProfileSidebarOpen(false)}
          />

          {/* Drawer Lateral */}
          <div className="fixed top-0 right-0 h-screen w-72 sm:w-80 md:w-84 bg-[#101114]/95 backdrop-blur-2xl border-l border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.8)] z-[100] flex flex-col p-6 sm:p-8 transition-transform duration-300 transform translate-x-0">
            {/* Header del Perfil */}
            <div className="flex items-center justify-between border-b-2 border-[#E44036] pb-3 mb-8">
              <div className="flex items-center gap-2.5 text-[#E44036] font-extrabold text-xl sm:text-2xl tracking-wider">
                <span>PERFIL</span>
                <div className="w-8 h-8 rounded-full border-2 border-[#E44036] flex items-center justify-center">
                  <PersonOutlinedIcon className="!text-xl" />
                </div>
              </div>
              <button
                onClick={() => setIsProfileSidebarOpen(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <ClearIcon />
              </button>
            </div>

            {/* Enlaces del Perfil */}
            <div className="flex flex-col gap-6 font-semibold text-base sm:text-lg">
              <button
                onClick={() => {
                  setIsProfileSidebarOpen(false);
                  navigate('/profile');
                }}
                className="text-left text-gray-200 hover:text-white transition-colors cursor-pointer"
              >
                Mi Perfil
              </button>

              <button
                onClick={() => {
                  setIsProfileSidebarOpen(false);
                  navigate('/my-tickets');
                }}
                className="text-left text-gray-200 hover:text-white transition-colors cursor-pointer"
              >
                Mis Tickets
              </button>
            </div>

            {/* Botón Inferior para Cerrar Sesión */}
            <div className="mt-auto pt-6 pb-2">
              <button
                onClick={() => {
                  setIsProfileSidebarOpen(false);
                  logout();
                }}
                className="w-full bg-[#E44036] hover:bg-[#c93229] active:scale-95 text-white py-3 px-6 rounded-full font-extrabold uppercase text-xs sm:text-sm tracking-wider transition-all transform hover:scale-105 shadow-lg shadow-red-900/30 cursor-pointer text-center"
              >
                CERRAR SESIÓN
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default NavBar;

